/* eslint-disable */
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from "@material-ui/core";
import {
  ChangeEvent,
  default as React,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { useReactToPrint } from "react-to-print";
import { usersService } from "../../../../../api";
import SvgIconBase from "../../../../../assets/icons/custom-svg-components/SvgIconBase";
import { ReactComponent as Print } from "../../../../../assets/icons/print.svg";
import { EventStatus } from "../../../../../models/enums/eventStatus.enum";
import {
  ELearningParticipationStatus,
  ParticipationStatus,
} from "../../../../../models/enums/participationStatus.enum";
import { TrainingType } from "../../../../../models/enums/trainingType.enum";
import { UserType } from "../../../../../models/enums/userType.enum";
import { EventDataDto } from "../../../../../models/eventData";
import { LecturesData } from "../../../../../models/lecturesData";
import { ParticipantListData } from "../../../../../models/participantData";
import { UserData } from "../../../../../models/userData";
import { AppState } from "../../../../../redux";

import {
  getParticipants,
  sendEmailToParticipants,
} from "../../../../../redux/participants/actions";

import usePermission from "../../../../../services/usePermissions";
import CustomTextField from "../../../../forms/CustomTextField";
import Loader from "../../../../theming/loader/Loader";
import { isEventOver } from "../checkEventIsOver";
import "./Participants.scss";
import { ParticipantsHeader } from "./ParticipantsHeader";
import { ParticipantsItem } from "./ParticipantsItem";
import { ParticipantsListPrint } from "./ParticipantsListPrint";
import { AddParticipant } from "./AddParticipant";

interface ParticipantsProps {
  eventData: EventDataDto | LecturesData;
  trainingType: TrainingType;
}

export const Participants: React.FC<ParticipantsProps> = (props) => {
  let { id } = useParams<{ id: string }>();

  const [open, setOpen] = React.useState(false);
  const [textValue, setTextValue] = React.useState("");

  let noParticipant = "Es gibt keine Teilnehmer für diese Veranstaltung.";
  if (props.trainingType === TrainingType.ELearning)
    noParticipant = "Es gibt keine aktiven Teilnehmer für dieses E-Learning.";
  if (props.trainingType === TrainingType.BlendedLearning)
    noParticipant = "Es gibt keine Teilnehmer für dieses Blended-Learning.";

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSend = () => {
    dispatch(sendEmailToParticipants(id, textValue));
    setOpen(false);
  };

  const handleParticipantMessageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTextValue(event.target.value);
  };

  const componentRef = useRef(null);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  const dispatch = useDispatch();
  const participants = useSelector((state: AppState) => state.participant);

  /* search with autocomplete */
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchOptions, setSearchOptions] = React.useState<UserData[]>([]);
  const [searchQuery, setSearchQuery] = React.useState("");

  const currentUser = useSelector((state: AppState) => state.user.currentUser);
  const { isStaff } = usePermission(currentUser?.user_type);

  const isCurrentParticipant = useCallback((participation: ParticipantListData) => {
    return ![
      ParticipationStatus.ReservedCart,
      ParticipationStatus.Cancelled,
      ParticipationStatus.CancelledByStaff,
    ].includes(participation.status);
  }, []);

  const isActiveParticipant = useCallback(
    (participation: ParticipantListData) => {
      return props.eventData.training_type === TrainingType.ELearning
        ? (participation.is_within_elearning_period &&
            participation.elearning_status !== ELearningParticipationStatus.Passed) ||
            participation.reactivated
        : participation.is_within_elearning_period ||
            isCurrentParticipant(participation);
    },
    [isCurrentParticipant, props.eventData.training_type]
  );

  const sortedList = (list: ParticipantListData[]) => {
    return list.sort((firstUser, secondUser) =>
      firstUser.user.last_name.localeCompare(secondUser.user.last_name)
    );
  };

  // put the calls into useMemo hook so the values are cached
  // as long as the participantList does not change
  const activeParticipants = useMemo(() => {
    return sortedList(
      participants.participantList.filter((participant) =>
        isActiveParticipant(participant)
      )
    );
  }, [isActiveParticipant, participants.participantList]);

  const cancelledParticipants = useMemo(() => {
    return sortedList(
      participants.participantList.filter(
        (participant) =>
          participant.status === ParticipationStatus.Cancelled ||
          participant.status === ParticipationStatus.CancelledByStaff
      )
    );
  }, [participants.participantList]);

  const isDisabledSelect = () => {
    if (
      props.eventData.status === EventStatus.Completed ||
      props.eventData.status === EventStatus.Cancelled ||
      props.trainingType === TrainingType.ELearning
    ) {
      return true;
    } else {
      isEventOver(props.eventData.end);
    }
    return false;
  };

  const handleSearchQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const [addParticipants, setAddParticipants] = useState(false);

  const checkParticipantParticipationStatus = () => {
    if (
      participants.participantList.filter(
        (participant) => participant.status === ParticipationStatus.Booked
      ).length === 0 &&
      participants.participantList.filter(
        (participant) =>
          participant.status === ParticipationStatus.Participated ||
          participant.status === ParticipationStatus.NotParticipated
      ).length > 0
    ) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      let query = searchQuery;

      if (query != null) {
        (async () => {
          if (query === searchQuery && query.length > 0) {
            let result: UserData[] = await usersService.getUsers(
              query,
              UserType.Participant,
              id
            );
            // filter out the current participations but keep blended-learning ones
            // because they include an e-learning-period which shoud be restartable (-> reactivated)
            if (props.eventData.training_type !== TrainingType.BlendedLearning) {
              // TODO: filtering should be done in backend
              result = result.filter(
                (item) =>
                  participants.participantList.filter(
                    (p) => p.user.id === item.id && isActiveParticipant(p)
                  ).length === 0
              );
            }
            setSearchOptions(result);
          }
        })();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [
    searchQuery,
    participants,
    id,
    setSearchOptions,
    isActiveParticipant,
    props.eventData.training_type,
  ]);

  useEffect(() => {
    if (!searchOpen) {
      setSearchOptions([]);
    }
  }, [searchOpen]);

  useEffect(() => {
    dispatch(getParticipants(id));
  }, [dispatch, id]);

  /////////////

  const [selectedActiveParticipants, setSelectedActiveParticipants] = useState<
    boolean[]
  >(new Array(activeParticipants.length).fill(false));

  const [selectedCancelledParticipants, setSelectedCancelledParticipants] = useState<
    boolean[]
  >(new Array(cancelledParticipants.length).fill(false));

  const [selectedActiveParticipantsForChange, setSelectedActiveParticipantsForChange] =
    useState<ParticipantListData[]>(
      activeParticipants.filter((d, ind) => selectedActiveParticipants[ind])
    );

  const [
    selectedCancelledParticipantsForChange,
    setSelectedCancelledParticipantsForChange,
  ] = useState<ParticipantListData[]>(
    cancelledParticipants.filter((d, ind) => selectedCancelledParticipants[ind])
  );

  // pre-fill the selected array with false values
  // if the number of elements in each list change (e.g. if an employee adds a participant)
  useEffect(() => {
    setSelectedActiveParticipants(new Array(activeParticipants.length).fill(false));

    setSelectedCancelledParticipants(
      new Array(cancelledParticipants.length).fill(false)
    );
  }, [activeParticipants.length, cancelledParticipants.length]);

  // do this so the participants for the multiselect checkbox of each list are set correctly
  useEffect(() => {
    setSelectedActiveParticipantsForChange(
      activeParticipants.filter((d, ind) => selectedActiveParticipants[ind])
    );
    setSelectedCancelledParticipantsForChange(
      cancelledParticipants.filter((d, ind) => selectedCancelledParticipants[ind])
    );
  }, [
    activeParticipants,
    cancelledParticipants,
    selectedActiveParticipants,
    selectedCancelledParticipants,
  ]);

  const handleActiveParticipantsClick = (index: number) => {
    setSelectedCancelledParticipants(
      new Array(cancelledParticipants.length).fill(false)
    );
    setSelectedActiveParticipants((prevState) =>
      prevState.map((item, idx) => (idx !== index ? item : !item))
    );
  };

  const handleCancelledParticipantsClick = (index: number) => {
    setSelectedActiveParticipants(new Array(activeParticipants.length).fill(false));
    setSelectedCancelledParticipants((prevState) =>
      prevState.map((item, idx) => (idx !== index ? item : !item))
    );
  };

  const handleSelectAllActiveParticipants = () => {
    setSelectedCancelledParticipants(
      new Array(cancelledParticipants.length).fill(false)
    );

    if (selectedActiveParticipantsForChange.length === activeParticipants.length) {
      setSelectedActiveParticipants((prevState) => prevState.map(() => false));
    } else {
      setSelectedActiveParticipants((prevState) => prevState.map(() => true));
    }
  };

  const handleSelectAllCancelledParticipants = () => {
    setSelectedActiveParticipants(new Array(activeParticipants.length).fill(false));

    if (selectedCancelledParticipantsForChange.length === cancelledParticipants.length) {
      setSelectedCancelledParticipants((prevState) => prevState.map(() => false));
    } else {
      setSelectedCancelledParticipants((prevState) => prevState.map(() => true));
    }
  };

  return (
    <>
      <Grid container spacing={4}>
        {participants.isLoading ? (
          <Loader />
        ) : (
          <>
            {/*Printable version of participant list */}
            <Box style={{ display: "none" }}>
              <ParticipantsListPrint
                ref={componentRef}
                participantList={participants.participantList.filter((participant) =>
                  isActiveParticipant(participant)
                )}
                eventData={props.eventData}
                currentUser={currentUser}
              />
            </Box>
            {/*End of Printable version of participant list */}
            <Grid item xs={12} sm={12}>
              <h3>
                Teilnahmeliste{" "}
                <Badge
                  color="secondary"
                  className={"participant-list-count-badge"}
                  max={999}
                  badgeContent={
                    participants.participantList.filter((participant) =>
                      isActiveParticipant(participant)
                    ).length
                  }
                  style={{
                    paddingLeft: "3.125rem",
                  }}
                  overlap="rectangular"
                />
                <Box
                  style={{
                    float: "right",
                    marginBottom: "1.25rem",
                  }}
                >
                  {isStaff() && (
                    <>
                      <Button onClick={handleClickOpen}>
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          size={"2x"}
                          color="#777"
                          style={{
                            fontSize: "1.56rem",
                            marginRight: ".4375rem",
                            verticalAlign: "middle",
                          }}
                        />
                        Teilnehmer kontaktieren
                      </Button>
                      <Dialog open={open} onClose={handleClose}>
                        <DialogTitle
                          style={{
                            marginLeft: "9.375rem",
                            marginRight: "9.375rem",
                          }}
                          id="alert-dialog-title"
                        >
                          {"Teilnehmer kontaktieren"}
                        </DialogTitle>
                        <DialogContent>
                          <CustomTextField
                            fullWidth
                            multiline
                            rows={6}
                            name="message"
                            label="Ihre Nachricht an alle Teilnehmer"
                            onChange={handleParticipantMessageChange}
                          />
                        </DialogContent>
                        <DialogActions>
                          <Button
                            onClick={handleClose}
                            variant="contained"
                            color="secondary"
                            style={{
                              marginBottom: ".625rem",
                            }}
                          >
                            Abbrechen
                          </Button>
                          <Button
                            autoFocus
                            variant="contained"
                            color="primary"
                            onClick={handleSend}
                            disabled={textValue.length < 10}
                            style={{
                              marginRight: "1.0625rem",
                              marginBottom: ".625rem",
                            }}
                          >
                            Absenden
                          </Button>
                        </DialogActions>
                      </Dialog>
                    </>
                  )}
                  {activeParticipants.length !== 0 && (
                    <IconButton
                      onClick={handlePrint}
                      aria-label="print participants"
                      style={{
                        cursor: "pointer",
                      }}
                      color={"inherit"}
                    >
                      <SvgIconBase
                        style={{ stroke: "var(--text-color)", fontSize: "1.56rem" }}
                        component={Print}
                      />
                    </IconButton>
                  )}
                </Box>
              </h3>
              <ParticipantsHeader
                participants={selectedActiveParticipantsForChange}
                index={11}
                trainingType={props.trainingType}
                checkHandler={handleSelectAllActiveParticipants}
                checked={selectedActiveParticipants.every((obj) => obj === true)}
                noParticipants={!activeParticipants.length}
                value={20}
              />
              {activeParticipants.length === 0
                ? noParticipant
                : sortedList(participants.participantList)
                    .filter((participant) => isActiveParticipant(participant))
                    .map((participant, index) => {
                      return (
                        <ParticipantsItem
                          participant={participant}
                          index={index}
                          key={participant.id}
                          disabledSelect={isDisabledSelect()}
                          trainingType={props.trainingType}
                          checkedHandler={() => handleActiveParticipantsClick(index)}
                          checked={selectedActiveParticipants[index] ?? false}
                        />
                      );
                    })}
              {props.trainingType !== TrainingType.ELearning &&
                currentUser?.user_type !== UserType.Speaker && (
                  <Grid item xs={12} sm={12}>
                    <h2>
                      Storniert{" "}
                      <Badge
                        color="secondary"
                        className={"participant-list-count-badge"}
                        badgeContent={
                          participants.participantList.filter(
                            (participant) =>
                              participant.status === ParticipationStatus.Cancelled ||
                              participant.status === ParticipationStatus.CancelledByStaff
                          ).length
                        }
                        overlap="rectangular"
                      />
                    </h2>
                    <ParticipantsHeader
                      participants={selectedCancelledParticipantsForChange}
                      index={33}
                      trainingType={props.trainingType}
                      checkHandler={handleSelectAllCancelledParticipants}
                      checked={selectedCancelledParticipants.every(
                        (obj) => obj === true
                      )}
                      noParticipants={!cancelledParticipants.length}
                      value={30}
                    />
                    {participants.participantList.filter(
                      (participant) =>
                        participant.status === ParticipationStatus.Cancelled ||
                        participant.status === ParticipationStatus.CancelledByStaff
                    ).length === 0
                      ? "Es gibt keine Teilnehmer auf der Stornoliste."
                      : cancelledParticipants.map((participant, index) => {
                          return (
                            <ParticipantsItem
                              participant={participant}
                              index={index}
                              key={"cancelledList" + index}
                              disabledSelect={isDisabledSelect()}
                              trainingType={props.trainingType}
                              checkedHandler={() =>
                                handleCancelledParticipantsClick(index)
                              }
                              checked={selectedCancelledParticipants[index] ?? false}
                            />
                          );
                        })}
                  </Grid>
                )}
            </Grid>
          </>
        )}
      </Grid>
      {/*
      TODO: Re-enable this button once the add participant functionality has been revised.
      <Button
        id="add-participant"
        variant="contained"
        color="primary"
        onClick={() => {
          setAddParticipants(!addParticipants);
        }}
        style={{ marginTop: "1.25rem" }}
      >
        Neuen Teilnehmer hinzufügen
      </Button>
      */}
      {addParticipants ? <AddParticipant event_id={props.eventData.id} /> : null}
    </>
  );
};

// TODO no translation yet
